import { Modal } from 'bootstrap';

import { getApiUrl } from '../utils/getApiUrl';

interface ResidentModalData {
  name: string;
  image: string;
  url?: string | null;
  description?: string;
}

interface ResidentApiResponse extends ResidentModalData {
  success?: boolean;
  msg?: string;
}

export class ResidencyResidentModalWidget {
  private modal: Modal | null = null;
  private modalEl: HTMLElement | null = null;
  private logoEl: HTMLImageElement | null = null;
  private nameEl: HTMLElement | null = null;
  private siteEl: HTMLAnchorElement | null = null;
  private siteTextEl: HTMLElement | null = null;
  private bodyEl: HTMLElement | null = null;
  private descriptionEl: HTMLElement | null = null;

  public init(root: ParentNode = document): void {
    this.modalEl = document.getElementById('residencyResidentModal');
    if (!this.modalEl) return;

    this.mountModalToBody();
    this.cacheElements();
    this.modal = Modal.getOrCreateInstance(this.modalEl);
    this.bindCards(root);
  }

  private mountModalToBody(): void {
    if (!this.modalEl || this.modalEl.parentElement === document.body) return;
    document.body.appendChild(this.modalEl);
  }

  private cacheElements(): void {
    if (!this.modalEl) return;

    this.logoEl = this.modalEl.querySelector('[data-residency-resident-modal-logo]');
    this.nameEl = this.modalEl.querySelector('[data-residency-resident-modal-name]');
    this.siteEl = this.modalEl.querySelector('[data-residency-resident-modal-site]');
    this.siteTextEl = this.modalEl.querySelector('[data-residency-resident-modal-site-text]');
    this.bodyEl = this.modalEl.querySelector('[data-residency-resident-modal-body]');
    this.descriptionEl = this.modalEl.querySelector('[data-residency-resident-modal-description]');
  }

  private bindCards(root: ParentNode): void {
    root.querySelectorAll<HTMLElement>('.resident-card--modal-trigger').forEach((card) => {
      if (card.dataset.residencyResidentBound === 'true') return;
      card.dataset.residencyResidentBound = 'true';

      card.addEventListener('click', () => {
        void this.openFromCard(card);
      });

      card.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        void this.openFromCard(card);
      });
    });
  }

  private readCardData(card: HTMLElement): ResidentModalData {
    const image =
      card.dataset.residentImage ??
      card.querySelector<HTMLImageElement>('.resident-card__photo')?.src ??
      '';

    return {
      name:
        card.dataset.residentName ??
        card.querySelector('.resident-card__name')?.textContent?.trim() ??
        '',
      image,
      url: card.dataset.residentUrl ?? '',
      description:
        card.querySelector('.resident-card__description-source')?.innerHTML.trim() ??
        card.dataset.residentDescription ??
        '',
    };
  }

  private hostnameFromUrl(url: string): string {
    try {
      const { hostname } = new URL(url, window.location.href);
      return hostname || url;
    } catch {
      return url;
    }
  }

  private async fetchResident(id: string): Promise<ResidentModalData> {
    const response = await fetch(getApiUrl(`/api/getResident.php?id=${encodeURIComponent(id)}`));
    if (!response.ok) {
      throw new Error(`Resident request failed: ${response.status}`);
    }

    const data = (await response.json()) as ResidentApiResponse;
    if (data.success === false || !data.name) {
      throw new Error(data.msg ?? 'Resident not found');
    }

    return {
      name: data.name,
      image: data.image,
      url: data.url,
      description: data.description,
    };
  }

  private async resolveCardData(card: HTMLElement): Promise<ResidentModalData> {
    const cardData = this.readCardData(card);
    const residentId = card.dataset.id?.trim();

    if (!residentId || cardData.description) {
      return cardData;
    }

    try {
      return await this.fetchResident(residentId);
    } catch {
      return cardData;
    }
  }

  private formatDescription(description: string): string {
    if (/<[a-z][\s\S]*>/i.test(description)) {
      return description;
    }

    return description
      .split(/\n\n+/)
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => `<p>${part}</p>`)
      .join('');
  }

  private render(data: ResidentModalData): void {
    if (this.nameEl) {
      this.nameEl.textContent = data.name;
    }

    if (this.logoEl) {
      if (data.image) {
        this.logoEl.src = data.image;
        this.logoEl.alt = data.name;
        this.logoEl.hidden = false;
      } else {
        this.logoEl.hidden = true;
      }
    }

    const url = data.url?.trim();
    if (this.siteEl && this.siteTextEl) {
      if (url) {
        this.siteEl.href = url;
        this.siteTextEl.textContent = this.hostnameFromUrl(url);
        this.siteEl.hidden = false;
      } else {
        this.siteEl.hidden = true;
      }
    }

    const description = data.description?.trim();
    if (this.bodyEl && this.descriptionEl) {
      if (description) {
        this.descriptionEl.innerHTML = this.formatDescription(description);
        this.bodyEl.hidden = false;
      } else {
        this.descriptionEl.innerHTML = '';
        this.bodyEl.hidden = true;
      }
    }
  }

  private async openFromCard(card: HTMLElement): Promise<void> {
    if (!this.modal) return;

    const data = await this.resolveCardData(card);
    this.render(data);
    this.modal.show();
  }
}
